import { PartImageItemBox } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { loadImage } from '@/components/modals/ImageEditorModal/imageExportUtils';
import { ImageEditorWorkspace, useImageEditorActions } from '@/components/modals/ImageEditorModal/ImageEditorWorkspace';
import { EditorImageState, EditorTool } from '@/components/modals/ImageEditorModal/types';
import { useEditorCanvasBounds } from '@/components/modals/ImageEditorModal/useEditorCanvasBounds';
import { useImageEditor } from '@/components/modals/ImageEditorModal/useImageEditor';
import { ApiErrorDisplay } from '@/types/apiError';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { boxOfCropRect, boxOfLasso, boxOfRegions, cropRectOfBox, EditCropPhoto, NaturalSize } from './editCrop';

const CANVAS_MAX_HEIGHT = 380;

export interface EditingCrop {
  itemIndex: number;
  label: string;
  description: string;
}

function useNaturalSize(file: File): NaturalSize | undefined {
  const [size, setSize] = useState<NaturalSize>();
  useEffect(() => {
    void loadImage(file).then(image => setSize({ width: image.naturalWidth, height: image.naturalHeight }));
  }, [file]);
  return size;
}

const BOX_OF_TOOL: Record<EditorTool, (state: EditorImageState, size: NaturalSize) => PartImageItemBox | undefined> = {
  segment: state => state.segmentation && boxOfRegions(state.segmentation, state.selectedRegionIds),
  lasso: (state, size) => boxOfLasso(state.lassoPoints, size),
  smartCrop: (state, size) => boxOfCropRect(state.cropRect, size),
};

const boxOfEdit = (state: EditorImageState | undefined, size: NaturalSize): PartImageItemBox | undefined =>
  state && BOX_OF_TOOL[state.activeTool](state, size);

interface PhotoCropEditorProperties {
  photo: EditCropPhoto;
  size: NaturalSize;
  onChange: (box: PartImageItemBox | undefined) => void;
}

const PhotoCropEditor = ({ photo, size, onChange }: PhotoCropEditorProperties) => {
  const initialCrop = useMemo(() => cropRectOfBox(photo.box, size), [photo.box, size]);
  const files = useMemo(() => [photo.file], [photo.file]);
  const editor = useImageEditor(files, initialCrop);
  const actions = useImageEditorActions(editor, initialCrop);
  const bounds = useEditorCanvasBounds(false);
  const box = boxOfEdit(editor.currentState, size);
  const boxKey = JSON.stringify(box);

  useEffect(() => {
    void actions.applyCrop(initialCrop);
  }, []);

  useEffect(() => onChange(box), [boxKey, onChange]);

  return (
    <Stack gap='12px' data-testid='part-image-edit-crop-editor'>
      <ImageEditorWorkspace
        editor={editor}
        actions={actions}
        bounds={{ ...bounds, maxHeight: Math.min(bounds.maxHeight, CANVAS_MAX_HEIGHT) }}
        previewLabel='New crop'
      />
    </Stack>
  );
};

const LoadedPhotoCropEditor = ({ photo, onChange }: Omit<PhotoCropEditorProperties, 'size'>) => {
  const size = useNaturalSize(photo.file);
  return size && <PhotoCropEditor photo={photo} size={size} onChange={onChange} />;
};

export interface EditCropViewProperties {
  editing: EditingCrop;
  photos: EditCropPhoto[];
  busy: boolean;
  error?: ApiErrorDisplay;
  onBack: () => void;
  onSave: (photoNumber: number, box: PartImageItemBox) => void;
}

export const EditCropView = ({ editing, photos, busy, error, onBack, onSave }: EditCropViewProperties) => {
  const [photoNumber, setPhotoNumber] = useState(photos[0]?.photoNumber);
  const [box, setBox] = useState<PartImageItemBox>();
  const photo = photos.find(found => found.photoNumber === photoNumber) ?? photos[0];
  const save = () => photo && box && onSave(photo.photoNumber, box);

  return (
    <Stack gap='12px' sx={{ flex: { md: 1 }, minHeight: 0 }} data-testid='part-image-edit-crop-view'>
      <Typography variant='body2' color='text.secondary'>
        {editing.description}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        Crop to just this part. Saving reads the crop and searches for the item from it.
      </Typography>
      {photos.length > 1 && (
        <ToggleButtonGroup
          size='small'
          exclusive
          value={photo?.photoNumber}
          onChange={(_event, chosen: unknown) => typeof chosen === 'number' && setPhotoNumber(chosen)}
        >
          {photos.map(choice => (
            <ToggleButton key={choice.photoNumber} value={choice.photoNumber}>
              Photo {choice.photoNumber}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      <ApiErrorAlert error={error} />
      <Box sx={{ flex: { md: 1 }, minHeight: 0, overflowY: 'auto' }}>
        {photo && <LoadedPhotoCropEditor key={photo.photoNumber} photo={photo} onChange={setBox} />}
      </Box>
      <Stack direction='row' gap='8px' justifyContent='flex-end'>
        <Button onClick={onBack} disabled={busy}>
          Cancel
        </Button>
        <Button variant='contained' disabled={busy || !box} onClick={save} data-testid='part-image-edit-crop-save'>
          Save crop
        </Button>
      </Stack>
    </Stack>
  );
};
